const API_BASE = process.env.NEXT_PUBLIC_API_URL || "";
const GATEWAY_BASE = process.env.NEXT_PUBLIC_GATEWAY_URL || "/v1";

async function request(path: string, options: RequestInit = {}) {
  const token = typeof window !== "undefined" ? localStorage.getItem("accessToken") : null;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 401 && typeof window !== "undefined") {
    // Try refresh
    const refreshToken = localStorage.getItem("refreshToken");
    if (refreshToken) {
      const refreshRes = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (refreshRes.ok) {
        const data = await refreshRes.json();
        localStorage.setItem("accessToken", data.accessToken);
        localStorage.setItem("refreshToken", data.refreshToken);
        headers["Authorization"] = `Bearer ${data.accessToken}`;
        return fetch(`${API_BASE}${path}`, { ...options, headers });
      }
    }
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    const locale = document.documentElement.lang || "en";
    window.location.href = `/${locale}/auth/login`;
  }

  return res;
}

export const api = {
  // Auth
  register: (data: { email: string; password: string; name: string }) =>
    fetch(`${API_BASE}/api/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }),
  login: (data: { email: string; password: string }) =>
    fetch(`${API_BASE}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }),

  // User
  getProfile: () => request("/api/user/profile"),
  updateProfile: (data: { name: string }) =>
    request("/api/user/profile", { method: "PUT", body: JSON.stringify(data) }),
  getBalance: () => request("/api/user/balance"),
  getUsage: () => request("/api/user/usage"),
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    request("/api/user/password", { method: "PUT", body: JSON.stringify(data) }),

  // Keys
  getKeys: () => request("/api/keys"),
  createKey: (name: string) =>
    request("/api/keys", { method: "POST", body: JSON.stringify({ name }) }),
  deleteKey: (id: string) =>
    request(`/api/keys/${id}`, { method: "DELETE" }),

  // Transactions
  getTransactions: (page = 1) => request(`/api/transactions?page=${page}`),
  manualTopup: (amount: number) =>
    request("/api/transactions/topup/manual", {
      method: "POST",
      body: JSON.stringify({ amount }),
    }),

  // Chat sessions
  getChats: (params?: { folderId?: string; q?: string }) => {
    const qs = new URLSearchParams();
    if (params?.folderId !== undefined) qs.set("folderId", params.folderId);
    if (params?.q) qs.set("q", params.q);
    const s = qs.toString();
    return request(`/api/chats${s ? `?${s}` : ""}`);
  },
  createChat: (data: { title?: string; model?: string; folderId?: string }) =>
    request("/api/chats", { method: "POST", body: JSON.stringify(data) }),
  updateChat: (id: string, data: { title?: string; model?: string; folderId?: string | null }) =>
    request(`/api/chats/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteChat: (id: string) =>
    request(`/api/chats/${id}`, { method: "DELETE" }),
  getMessages: (chatId: string) => request(`/api/chats/${chatId}/messages`),
  saveMessage: (chatId: string, data: { role: string; content: string; model?: string }) =>
    request(`/api/chats/${chatId}/messages`, { method: "POST", body: JSON.stringify(data) }),
  sendMessage: (chatId: string, content: string, attachments?: { name: string; data: string; mimeType: string; isImage: boolean; isPdf?: boolean }[]) =>
    request(`/api/chats/${chatId}/send`, { method: "POST", body: JSON.stringify({ content, attachments }) }),

  // Chat folders
  getFolders: () => request("/api/chat-folders"),
  createFolder: (name: string) =>
    request("/api/chat-folders", { method: "POST", body: JSON.stringify({ name }) }),
  updateFolder: (id: string, data: { name?: string; instructions?: string }) =>
    request(`/api/chat-folders/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteFolder: (id: string) =>
    request(`/api/chat-folders/${id}`, { method: "DELETE" }),

  // Folder files
  getFolderFiles: (folderId: string) =>
    request(`/api/chat-folders/${folderId}/files`),
  uploadFolderFile: (folderId: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const token = typeof window !== "undefined" ? localStorage.getItem("accessToken") : null;
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${API_BASE}/api/chat-folders/${folderId}/files`, {
      method: "POST",
      headers,
      body: formData,
    });
  },
  deleteFolderFile: (folderId: string, fileId: string) =>
    request(`/api/chat-folders/${folderId}/files/${fileId}`, { method: "DELETE" }),

  // Task context optimization (Phase 3)
  createTask: (data: { title?: string; model?: string; folderId?: string }) =>
    request("/api/tasks", { method: "POST", body: JSON.stringify(data) }),
  getTaskContext: (taskId: string) =>
    fetch(`${API_BASE}/api/tasks/${taskId}/context`).then(r => r.json()),
  closeTask: (taskId: string) =>
    request(`/api/tasks/${taskId}/close`, { method: "PUT" }),

  // Chat data management (Export, Import, Delete)
  exportChats: () =>
    request("/api/chats/export", { method: "POST" }),
  importChats: (chats: any[]) =>
    request("/api/chats/import", { method: "POST", body: JSON.stringify({ chats }) }),
  deleteAllChats: () =>
    request("/api/chats/all", { method: "DELETE" }),
};

// Chat completions via gateway
export async function chatCompletion(
  apiKey: string,
  model: string,
  messages: { role: string; content: string }[],
  options: { stream?: boolean; maxTokens?: number; temperature?: number; taskId?: string } = {}
) {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${apiKey}`,
  };

  // Add X-Task-ID header for token optimization (Phase 3)
  if (options.taskId) {
    headers["X-Task-ID"] = options.taskId;
  }

  return fetch(`${GATEWAY_BASE}/chat/completions`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      model,
      messages,
      stream: options.stream ?? false,
      max_tokens: options.maxTokens ?? 1024,
      temperature: options.temperature ?? 0.7,
    }),
  });
}
